import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  GetEventTransactionDetailsRequest,
  EventTransactionDetailsData,
} from "@/modules/eventRegistration/features/EventWaitlist/api/getEventTransactionDetails/types";
import {
  InitiatePaymentRefundRequest,
  PaymentRefundData,
} from "@/modules/eventRegistration/features/EventWaitlist/api/initiatePaymentRefund/types";
import {
  ReturnEventVouchersRequest,
  VoucherReturnData,
} from "@/modules/eventRegistration/features/EventWaitlist/api/returnEventVouchers/types";
import { getEventTransactionDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/getEventTransactionDetails/getEventTransactionDetailsApi";
import { initiatePaymentRefund } from "@/modules/eventRegistration/features/EventWaitlist/api/initiatePaymentRefund/initiatePaymentRefundApi";
import { returnEventVouchers } from "@/modules/eventRegistration/features/EventWaitlist/api/returnEventVouchers/returnEventVouchersApi";

interface EventTransactionsSliceState {
  // Transaction Details State
  loadingTransactionDetails: boolean;
  transactionDetailsError: string | null;
  transactionDetails: EventTransactionDetailsData | null;

  // Payment Refund State
  loadingPaymentRefund: boolean;
  paymentRefundError: string | null;
  paymentRefundResult: PaymentRefundData | null;

  // Voucher Return State
  loadingVoucherReturn: boolean;
  voucherReturnError: string | null;
  voucherReturnResult: VoucherReturnData | null;
}

const initialState: EventTransactionsSliceState = {
  loadingTransactionDetails: false,
  transactionDetailsError: null,
  transactionDetails: null,

  loadingPaymentRefund: false,
  paymentRefundError: null,
  paymentRefundResult: null,

  loadingVoucherReturn: false,
  voucherReturnError: null,
  voucherReturnResult: null,
};

const eventTransactionsSlice = createSlice({
  name: "eventTransactions",
  initialState,
  reducers: {
    // Transaction Details Reducers
    setLoadingTransactionDetails(state, action: PayloadAction<boolean>) {
      state.loadingTransactionDetails = action.payload;
    },
    setTransactionDetailsError(state, action: PayloadAction<string | null>) {
      state.transactionDetailsError = action.payload;
    },
    setTransactionDetails(
      state,
      action: PayloadAction<EventTransactionDetailsData | null>,
    ) {
      state.transactionDetails = action.payload;
    },

    // Payment Refund Reducers
    setLoadingPaymentRefund(state, action: PayloadAction<boolean>) {
      state.loadingPaymentRefund = action.payload;
    },
    setPaymentRefundError(state, action: PayloadAction<string | null>) {
      state.paymentRefundError = action.payload;
    },
    setPaymentRefundResult(
      state,
      action: PayloadAction<PaymentRefundData | null>,
    ) {
      state.paymentRefundResult = action.payload;
    },

    // Voucher Return Reducers
    setLoadingVoucherReturn(state, action: PayloadAction<boolean>) {
      state.loadingVoucherReturn = action.payload;
    },
    setVoucherReturnError(state, action: PayloadAction<string | null>) {
      state.voucherReturnError = action.payload;
    },
    setVoucherReturnResult(
      state,
      action: PayloadAction<VoucherReturnData | null>,
    ) {
      state.voucherReturnResult = action.payload;
    },

    // Clear State
    clearEventTransactionsState() {
      return initialState;
    },
  },
});

export default eventTransactionsSlice.reducer;

export const {
  setLoadingTransactionDetails,
  setTransactionDetailsError,
  setTransactionDetails,
  setLoadingPaymentRefund,
  setPaymentRefundError,
  setPaymentRefundResult,
  setLoadingVoucherReturn,
  setVoucherReturnError,
  setVoucherReturnResult,
  clearEventTransactionsState,
} = eventTransactionsSlice.actions;

// Async Actions

export const fetchEventTransactionDetailsAction =
  (requestData: GetEventTransactionDetailsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingTransactionDetails(true));
      dispatch(setTransactionDetailsError(null));
      dispatch(setTransactionDetails(null));

      const response = await getEventTransactionDetails(requestData);
      dispatch(setTransactionDetails(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch event transaction details.";
      dispatch(setTransactionDetailsError(message));
    } finally {
      dispatch(setLoadingTransactionDetails(false));
    }
  };

export const initiatePaymentRefundAction =
  (
    requestData: InitiatePaymentRefundRequest,
    onSuccess?: (result: PaymentRefundData) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingPaymentRefund(true));
      dispatch(setPaymentRefundError(null));
      dispatch(setPaymentRefundResult(null));

      const response = await initiatePaymentRefund(requestData);
      dispatch(setPaymentRefundResult(response.data));

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to initiate payment refund.";
      dispatch(setPaymentRefundError(message));
    } finally {
      dispatch(setLoadingPaymentRefund(false));
    }
  };

export const returnEventVouchersAction =
  (
    requestData: ReturnEventVouchersRequest,
    onSuccess?: (result: VoucherReturnData) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingVoucherReturn(true));
      dispatch(setVoucherReturnError(null));
      dispatch(setVoucherReturnResult(null));

      const response = await returnEventVouchers(requestData);
      dispatch(setVoucherReturnResult(response.data));

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to return event vouchers.";
      dispatch(setVoucherReturnError(message));
    } finally {
      dispatch(setLoadingVoucherReturn(false));
    }
  };
